import { useEffect, useState } from "react";
import { Eye, MessageCircle, ThumbsUp, Youtube } from "lucide-react";
import { fetchSocialYoutube } from "../../../api";
import type { SocialYoutubeCanal, SocialYoutubePayload, SocialYoutubeVideo } from "../../../types";
import { fechaCorta } from "../fechaCorta";
import { useIsMobile } from "../mobile/MobileChrome";
import type { SandboxTheme } from "../sandbox/SandboxResumen";
import "../mensajes/mensajes.css";
import "./youtube.css";

const num = (v: number | null | undefined) => (v ?? 0).toLocaleString("es-CL");
const compacto = (v: number | null | undefined) => {
  const n = v ?? 0;
  const uno = (x: number) => x.toLocaleString("es-CL", { maximumFractionDigits: 1 });
  if (n >= 1_000_000) return `${uno(n / 1_000_000)} M`;
  if (n >= 1_000) return `${uno(n / 1_000)} mil`;
  return num(n);
};
const fecha = (iso: string | null) => (iso ? iso.slice(0, 10) : "—");
const enlace = (v: SocialYoutubeVideo) => `https://www.youtube.com/watch?v=${encodeURIComponent(v.video_id)}`;

function Kpis({ canal, compact }: { canal: SocialYoutubeCanal; compact: boolean }) {
  const cifra = compact ? compacto : num;
  const ultimo = canal.serie[canal.serie.length - 1];
  const dias = canal.serie.length - 1;
  const delta = dias > 0 ? (ultimo.suscriptores ?? 0) - (canal.serie[0].suscriptores ?? 0) : null;
  return (
    <div className="msj-kpis yt-kpis">
      <div className="msj-kpi">
        <div className="l">Suscriptores</div>
        <div className="v">
          {num(ultimo.suscriptores)}
          {delta !== null && <small>{delta >= 0 ? "+" : ""}{num(delta)} en {dias} {dias === 1 ? "día" : "días"}</small>}
        </div>
      </div>
      <div className="msj-kpi">
        <div className="l">Vistas</div>
        <div className="v">{cifra(ultimo.vistas)}</div>
      </div>
      <div className="msj-kpi">
        <div className="l">Videos</div>
        <div className="v">{num(ultimo.videos)}</div>
      </div>
    </div>
  );
}

/** Portada del video: la imagen pública de YouTube, sin llamar a su API. */
function Portada({ v }: { v: SocialYoutubeVideo }) {
  return (
    <img
      className="yt-thumb"
      src={`https://i.ytimg.com/vi/${encodeURIComponent(v.video_id)}/mqdefault.jpg`}
      alt=""
      width={320}
      height={180}
      loading="lazy"
      decoding="async"
    />
  );
}

function TarjetaVideo({ v }: { v: SocialYoutubeVideo }) {
  return (
    <a className="yt-card" href={enlace(v)} target="_blank" rel="noreferrer">
      <Portada v={v} />
      <span className="yt-card-b">
        <span className="yt-card-t">{v.titulo ?? v.video_id}</span>
        <span className="yt-card-m">
          <span className="yt-stat" aria-label="Vistas"><Eye size={15} aria-hidden="true" />{compacto(v.vistas)}</span>
          <span className="yt-stat" aria-label="Likes"><ThumbsUp size={15} aria-hidden="true" />{compacto(v.likes)}</span>
          <span className="yt-stat" aria-label="Comentarios"><MessageCircle size={15} aria-hidden="true" />{compacto(v.comentarios)}</span>
          <span className="yt-fecha">{fechaCorta(v.publicado)}</span>
        </span>
      </span>
    </a>
  );
}

function TablaVideos({ videos, titulos, conCanal }: { videos: SocialYoutubeVideo[]; titulos: Map<string, string>; conCanal: boolean }) {
  return (
    <div className="msj-card">
      <table className="msj-tabla">
        <thead>
          <tr>
            <th>Video</th>
            {conCanal && <th>Canal</th>}
            <th>Publicado</th>
            <th>Vistas</th>
            <th>Likes</th>
            <th>Comentarios</th>
          </tr>
        </thead>
        <tbody>
          {videos.map((v) => (
            <tr key={v.video_id}>
              <td>
                <a className="yt-vid" href={enlace(v)} target="_blank" rel="noreferrer">
                  <Portada v={v} />
                  <span>{v.titulo ?? v.video_id}</span>
                </a>
              </td>
              {conCanal && <td>{titulos.get(v.channel_id) ?? v.channel_id}</td>}
              <td>{fecha(v.publicado)}</td>
              <td>{num(v.vistas)}</td>
              <td>{num(v.likes)}</td>
              <td>{num(v.comentarios)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Audiencia del canal de YouTube: suscriptores, vistas y los videos con más vistas. */
export function AudienciaYoutube({ clientSlug, theme = "dark" }: { clientSlug: string; theme?: SandboxTheme }) {
  const movil = useIsMobile();
  const [datos, setDatos] = useState<SocialYoutubePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [canalId, setCanalId] = useState<string | null>(null);

  useEffect(() => {
    let activo = true;
    setDatos(null);
    setError(null);
    setCanalId(null);
    fetchSocialYoutube(clientSlug)
      .then((r) => activo && setDatos(r))
      .catch((e: unknown) => activo && setError(e instanceof Error ? e.message : "Error desconocido"));
    return () => {
      activo = false;
    };
  }, [clientSlug]);

  const canales = datos?.canales ?? [];
  const titulos = new Map(canales.map((c) => [c.channel_id, c.titulo ?? c.channel_id]));
  // En el teléfono se ve un canal a la vez; en escritorio, todos juntos.
  const activo = movil ? (canales.find((c) => c.channel_id === canalId) ?? canales[0]) : null;
  const visibles = activo ? [activo] : canales;
  const videos = (datos?.videos ?? []).filter((v) => !activo || v.channel_id === activo.channel_id);

  return (
    <div className="sbx msj yt" data-theme={theme}>
      {!movil && (
        <header className="msj-head">
          <div className="msj-head-t"><h1 className="yt-titulo"><Youtube size={28} aria-hidden="true" />Audiencia en YouTube</h1></div>
        </header>
      )}
      {error && <div className="msj-vacio" role="alert">No se pudo cargar YouTube: {error}</div>}
      {!error && !datos && <div className="msj-vacio" role="status">Cargando…</div>}
      {datos && canales.length === 0 && <div className="msj-vacio">Sin datos de YouTube para este cliente.</div>}
      {movil && canales.length > 1 && (
        <div className="yt-canales" role="group" aria-label="Canal">
          {canales.map((c) => (
            <button key={c.channel_id} type="button" aria-pressed={c.channel_id === activo?.channel_id} onClick={() => setCanalId(c.channel_id)}>
              {c.titulo ?? c.channel_id}
            </button>
          ))}
        </div>
      )}
      {visibles.map((c) => (
        <section key={c.channel_id} aria-label={c.titulo ?? c.channel_id}>
          {(!movil || canales.length === 1) && <div className="msj-sec-h"><h2>{c.titulo ?? c.channel_id}</h2></div>}
          <Kpis canal={c} compact={movil} />
        </section>
      ))}
      {videos.length > 0 && (
        <section aria-label="Videos con más vistas">
          <div className="msj-sec-h"><h2>Videos con más vistas</h2></div>
          {movil ? (
            <div className="yt-lista">{videos.map((v) => <TarjetaVideo key={v.video_id} v={v} />)}</div>
          ) : (
            <TablaVideos videos={videos} titulos={titulos} conCanal={canales.length > 1} />
          )}
        </section>
      )}
    </div>
  );
}
